<template>
  <el-dialog
    :visible.sync="dialogVisible"
    :show-close="true"
    custom-class="renew-payment-dialog"
    :z-index="9999"
    @close="closeDialog"
  >
    <template v-slot:title>
      <h3>Payment Summary - Membership Renewal</h3>
    </template>

    <div class="dialog-content">
      <!-- Steps -->
      <el-steps
  :active="currentStep"
  finish-status="success"
  simple
  class="custom-steps"
>
  <el-step v-if="hasAdditionalQuestions">
    <template slot="title">
      <div class="step-wrapper">
        <div class="step-icon" :class="{ 'is-success': currentStep > additionalQuestionsStepNumber }">
          <i v-if="currentStep > additionalQuestionsStepNumber" class="el-icon-check"></i>
          <span v-else>{{ additionalQuestionsStepNumber }}</span>
        </div>
        <span class="step-text">Additional Questions</span>
      </div>
    </template>
  </el-step>
  <el-step v-if="hasSaleableItems">
    <template slot="title">
      <div class="step-wrapper">
        <div class="step-icon" :class="{ 'is-success': currentStep > saleableItemsStepNumber }">
          <i v-if="currentStep > saleableItemsStepNumber" class="el-icon-check"></i>
          <span v-else>{{ saleableItemsStepNumber }}</span>
        </div>
        <span class="step-text">Saleable Items</span>
      </div>
    </template>
  </el-step>
  <el-step >
    <template slot="title">
      <div class="step-wrapper">
        <div class="step-icon" :class="{ 'is-success': currentStep > summaryStepNumber }">
          <i v-if="currentStep > summaryStepNumber" class="el-icon-check"></i>
          <span v-else>{{ summaryStepNumber }}</span>
        </div>
        <span class="step-text">Summary</span>
      </div>
    </template>
  </el-step>
  <el-step>
    <template slot="title">
      <div class="step-wrapper">
        <div class="step-icon" :class="{ 'is-success': currentStep > paymentStepNumber }">
          <i v-if="currentStep > paymentStepNumber" class="el-icon-check"></i>
          <span v-else>{{ paymentStepNumber }}</span>
        </div>
        <span class="step-text">Payment</span>
      </div>
    </template>
  </el-step>
  <el-step>
    <template slot="title">
      <div class="step-wrapper">
        <div class="step-icon" :class="{ 'is-success': currentStep > confirmationStepNumber }">
          <i v-if="currentStep > confirmationStepNumber" class="el-icon-check"></i>
          <span v-else>{{ confirmationStepNumber }}</span>
        </div>
        <span class="step-text">Confirmation</span>
      </div>
    </template>
  </el-step>
</el-steps>

      <!-- Step content wrapper -->
      <div class="step-content">

        <!-- Step 2: Additional Questions -->
        <div v-show="currentStep === 1 && hasAdditionalQuestions">
          <!-- <h2 class="section-title">Additional Questions</h2> -->
          <!-- <p class="text-muted">The entity requires the following additional information as part of your registration</p> -->
          <additional-questions
            v-model="registration"
            ref="additionalQuestions"
            @update-step="handleAdditionalQuestionsComplete"
          />
        </div>

        <div v-show="currentStep === (hasAdditionalQuestions ? 2 : 1) && hasSaleableItems">
          <saleable-items
            v-model="registration"
            ref="saleableItems"
            @update-step="handleSaleableItemsComplete"
          />
        </div>
        <!-- Step 1: Summary -->
        <!-- Update the Summary section -->
        <div v-show="isSummaryStep" class="summary-section">
  <h2 class="section-title">Payment Summary</h2>

  <div class="summary-card">
    <!-- Membership Details -->
    <div class="summary-group">
      <h3 class="group-title">Membership Details</h3>
      <div class="summary-item">
        <span class="item-label">Membership Type</span>
        <span class="item-value">{{ rego.regoEntity.name }}</span>
      </div>
      <div class="summary-item">
        <span class="item-label">Member Category</span>
        <span class="item-value">{{ rego.memberType | cap }}</span>
      </div>
      <div class="summary-item">
        <span class="item-label">Membership Fee</span>
        <span class="item-value">{{ parsePrice(getMembershipPrice(rego)) }}</span>
      </div>
    </div>

    <!-- Saleable Items -->
    <div class="summary-group" v-if="registration.saleableItems?.length">
  <h3 class="group-title">Additional Items</h3>
  <div class="summary-item" v-for="item in registration.saleableItems" :key="item.id">
    <span class="item-label">
      {{ item.name }} {{ item.qty > 1 ? `(${item.qty}x)` : '' }}
    </span>
    <span class="item-value">{{ parsePrice(item.price * (item.qty || 1)) }}</span>
  </div>
</div>

    <!-- Total Section -->
    <div class="summary-total">
      <div class="total-item">
        <span class="total-label">Total Payment</span>
        <span class="total-value">{{ parsePrice(payTodayTotal) }}</span>
      </div>
      <div class="transaction-fee" v-if="transactionFee || (fixedTechFee && fixedTechFee.enabled)">
        <span v-if="fixedTechFee && fixedTechFee.enabled">
          *A {{transactionFee}}% transaction fee and a technology fee per registration will apply to online payments.
        </span>
        <span v-else>
          *Includes a {{transactionFee}}% transaction fee
        </span>
      </div>
    </div>
  </div>
</div>

        <!-- Step 3: Payment -->
        <div v-show="isPaymentStep">
          <el-form ref="form" :model="payment" :rules="rules">
            <div>
              <h2 class="section-title">Credit Card Details</h2>
              <small class="text-muted">*International Credit Cards are not accepted</small>
              <credit-card v-model="payment.card" ref="card" :width="300" data-dd-privacy="hidden"/>
              <el-divider />
              <div class="d-flex align-items-center" style="height: 30px; margin-bottom: 20px;">
                <h3 class="f-light mr-10">
                  Billing Address
                </h3>
                <el-button
                  class="py-0 fs-09 op-7 manualAddressBtn"
                  type="text"
                  @click="manualAddress"
                >
                  {{
                    payment.billingAddress.isManual
                      ? "Search Address"
                      : "Enter Address Manually"
                  }}
                </el-button>
              </div>

              <div class="mt--10" v-if="fillingAddress">
                <span
                  type="text"
                  class="text-underline c-highlight fs-09 pointer"
                  @click="fillAddress(fillingAddress)"
                >
                  Fill address with {{ fillingAddress.formatted }}
                </span>
              </div>

              <el-form-item prop="profile.address.formatted">
                <el-input
                  v-if="payment.billingAddress.isManual"
                  class="mt-05"
                  v-model="payment.billingAddress.formatted"
                  disabled
                />
                <google-auto-complete
                  v-else
                  class="auto-complete mt-05"
                  id="map"
                  :key="1"
                  ref="address"
                  @change="checkAddress($event)"
                  @blur="validateAddress"
                  @placechanged="getAddressData"
                  v-model="payment.billingAddress.formatted"
                  placeholder="Enter an Address"
                >
                </google-auto-complete>
              </el-form-item>

              <el-row :gutter="20" v-if="payment.billingAddress.isManual">
                <el-col :span="24" :md="12" class="mt-10">
                  <el-form-item prop="billingAddress.addressLine1" label="Address Line 1">
                    <el-input
                      class="mt-05"
                      v-model="payment.billingAddress.addressLine1"
                      placeholder="Type here..."
                    />
                  </el-form-item>
                </el-col>
                <el-col :span="24" :md="12" class="mt-10">
                  <el-form-item prop="billingAddress.addressLine2" label="Address Line 2">
                    <el-input
                      class="mt-05"
                      v-model="payment.billingAddress.addressLine2"
                      placeholder="Type here..."
                    />
                  </el-form-item>
                </el-col>
                <el-col :span="24" :md="12" class="mt-10">
                  <el-form-item prop="billingAddress.suburb" label="City/Suburb">
                    <el-input
                      class="mt-05"
                      v-model="payment.billingAddress.suburb"
                      placeholder="Type here..."
                    />
                  </el-form-item>
                </el-col>
                <el-col :span="24" :md="12" class="mt-10">
                  <el-form-item prop="billingAddress.state" label="State">
                    <el-input
                      class="mt-05"
                      v-model="payment.billingAddress.state"
                      placeholder="Type here..."
                    />
                  </el-form-item>
                </el-col>
                <el-col :span="24" :md="12" class="mt-10">
                  <el-form-item prop="billingAddress.postcode" label="Postcode">
                    <el-input
                      class="mt-05"
                      v-model="payment.billingAddress.postcode"
                      placeholder="Type here..."
                    />
                  </el-form-item>
                </el-col>
                <el-col :span="24" :md="12" class="mt-10">
                  <el-form-item prop="billingAddress.country" label="Country">
                    <el-select
                      filterable
                      remote
                      reserve-keyword
                      class="mt-05 w-100"
                      v-model="payment.billingAddress.country"
                      :remote-method="searchCountry"
                      placeholder="Select"
                    >
                      <el-option
                        v-for="item in countryItems"
                        :key="item.value"
                        :label="item.label"
                        :value="item.value">
                      </el-option>
                    </el-select>
                  </el-form-item>
                </el-col>
              </el-row>
            </div>
          </el-form>
        </div>

        <!-- Step 4: Confirmation -->
        <div v-show="isConfirmationStep" class="confirmation-section">
          <div class="confirmation-card">
            <div class="success-icon">
              <i class="el-icon-success"></i>
            </div>
            <h2 class="confirmation-title">Payment Successful!</h2>
            <p class="confirmation-message">
              Your membership has been successfully renewed. Your payment of {{ parsePrice(payTodayTotal) }} has been processed.
            </p>

            <div class="confirmation-details">
              <div class="detail-item">
                <strong>Membership Name:</strong> {{ rego.regoEntity.name }}
              </div>
              <div class="detail-item">
                <strong>Member Type:</strong> {{ rego.memberType | cap }}
              </div>
              <div class="detail-item">
                <strong>Payment Amount:</strong> {{ parsePrice(payTodayTotal) }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="dialog-footer">
  <div class="left-buttons">
    <el-button v-if="!isConfirmationStep" @click="closeDialog">Cancel</el-button>
    <el-button v-if="isConfirmationStep" @click="closeDialog">Close</el-button>
  </div>
  <div class="right-buttons">
    <el-button
      v-if="currentStep > 1 && !isConfirmationStep"
      @click="prevStep"
    >
      Back
    </el-button>
    <el-button
      v-if="isConfirmationStep"
      type="primary"
      @click="goToDashboard"
    >
      Back to Dashboard
    </el-button>
    <el-button
      v-if="!isConfirmationStep"
      type="primary"
      @click="isPaymentStep ? submitPayment() : nextStep()"
    >
      {{ isPaymentStep ? 'Make Payment' : 'Next' }}
    </el-button>
  </div>
</div>
    </div>
  </el-dialog>
</template>

<script>
import _ from 'lodash';
import { errormsg, countries } from '@/utils/constants';
import AdditionalQuestions from '@/views/registration/membership/individual/2-AdditionalQuestions.vue';
import SaleableItems from '@/views/registration/membership/individual/4-Store.vue';
// import CreditCard from '@/components/CreditCard.vue';

const defaultRequiredRule = { required: true, trigger: 'blur', message: errormsg.input_option };
const pin = new Pin.Api(process.env.VUE_APP_PIN_PK, process.env.VUE_APP_PIN_ENV);

const parstAddressFormatted = (address) => {
  const {
    addressLine1,
    addressLine2,
    suburb,
    state,
    postcode,
    country
  } = address;
  return `${addressLine1}${
    addressLine2 ? ` ${addressLine2}` : ''
  }, ${suburb} ${state}, ${postcode}, ${country}`;
};

export default {
  name: 'RenewPayment',
  components: {
    AdditionalQuestions,
    SaleableItems,
    // CreditCard,
  },
  props: {
    value: {
      type: Object,
      default: () => ({
        filtersVisible: false,
        entityType: undefined,
        registration: undefined,
        regoDetails: undefined,
        member: undefined,
      })
    },
    rego: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      currentStep: 1,
      dialogVisible: false,
      pin,
      additionalQuestionsCompleted: false,
      saleableItemsCompleted: false,
      payment: {
        card: {},
        billingAddress: {
          isManual: false,
          formatted: '',
          addressLine1: '',
          addressLine2: '',
          suburb: '',
          state: '',
          postcode: '',
          country: '',
        }
      },
      registration: {
        meta: {},
        saleableItems: []
      },
      rules: {
        'billingAddress.addressLine1': defaultRequiredRule,
        'billingAddress.suburb': defaultRequiredRule,
        'billingAddress.state': defaultRequiredRule,
        'billingAddress.postcode': defaultRequiredRule,
        'billingAddress.country': defaultRequiredRule,
      },
      countryItems: [],
      countries,
    };
  },
  mounted() {
    console.log('rego', this.rego);
    console.log('RenewPayment mounted');
    console.log('Initial registration:', this.registration);
    console.log('Initial rego:', this.rego);

    // Initialize meta properly to avoid map errors
    this.registration.meta = this.rego.meta || {};
    this.registration.saleableItems = this.rego.regoEntity.saleableItems;
    this.registration.additionalQs = this.rego.regoEntity.additionalQs;

    // Pre-initialize file upload fields as arrays to prevent map errors
    if (this.registration.additionalQs && Array.isArray(this.registration.additionalQs)) {
      this.registration.additionalQs.forEach((q) => {
        if (q.type === 3) { // File upload type
          const metaField = _.camelCase(q.fieldName);
          if (!this.registration.meta[metaField] || !Array.isArray(this.registration.meta[metaField])) {
            this.$set(this.registration.meta, metaField, []);
          }
        }
      });
    }
  },
  computed: {
    hasAdditionalQuestions() {
      const additionalQs = _.get(this.rego, 'regoEntity.additionalQs', []);
      const renewalToggle = _.get(this.rego, 'regoEntity.renewalAdditionalQs', false);
      return renewalToggle && additionalQs && additionalQs.length > 0;
    },
    hasSaleableItems() {
      const saleableItems = _.get(this.rego, 'regoEntity.saleableItems', []);
      return saleableItems && saleableItems.length > 0;
    },
    totalSteps() {
      return 3 + (this.hasAdditionalQuestions ? 1 : 0) + (this.hasSaleableItems ? 1 : 0);
    },
    isSummaryStep() {
      return this.currentStep === this.totalSteps - 2;
    },
    isPaymentStep() {
      return this.currentStep === this.totalSteps - 1;
    },
    isConfirmationStep() {
      return this.currentStep === this.totalSteps;
    },
    stepNumber() {
      // Dynamic step numbering
      let stepNum = 1;
      const steps = [];

      if (this.hasAdditionalQuestions) {
        steps.push({ type: 'additional', number: stepNum++ });
      }
      if (this.hasSaleableItems) {
        steps.push({ type: 'saleable', number: stepNum++ });
      }
      steps.push({ type: 'summary', number: stepNum++ });
      steps.push({ type: 'payment', number: stepNum++ });
      steps.push({ type: 'confirmation', number: stepNum++ });

      return steps;
    },
    currentStepNumber() {
      const steps = this.stepNumber;
      if (this.currentStep === 1 && this.hasAdditionalQuestions) {
        return steps.find((s) => s.type === 'additional')?.number || 1;
      }
      if (this.currentStep === (this.hasAdditionalQuestions ? 2 : 1) && this.hasSaleableItems) {
        return steps.find((s) => s.type === 'saleable')?.number || 1;
      }
      if (this.isSummaryStep) {
        return steps.find((s) => s.type === 'summary')?.number || 1;
      }
      if (this.isPaymentStep) {
        return steps.find((s) => s.type === 'payment')?.number || 1;
      }
      return 1;
    },
    payTodayTotal() {
      let total = this.getMembershipPrice(this.rego);
      if (this.registration.saleableItems?.length) {
        total += this.registration.saleableItems.reduce((sum, item) => sum + (item.price * (item.qty || 1)), 0);
      }
      const feePercent = this.transactionFee;
      // calculate fee
      const fee = (total * feePercent) / 100;
      total += fee;

      // Add fixed tech fee if enabled
      const fixedTechFee = this.$store.getters['national/getFixedTechFee'];
      if (fixedTechFee && fixedTechFee.enabled === true) {
        total += fixedTechFee.amount * 100; // Convert dollars to cents - Single registration for renewal
      }

      // round to make it integer
      return Math.round(total);
    },
    transactionFee() {
      let percent = this.$store.getters['cart/transactionFee'];
      if (!percent) {
        const nationalMeta = this.$store.getters['national/getMeta'];
        if (nationalMeta && nationalMeta.transactionFee) {
          percent = +nationalMeta.transactionFee;
        }
      }
      return percent;
    },
    debugStepInfo() {
      return {
        currentStep: this.currentStep,
        totalSteps: this.totalSteps,
        hasAdditionalQuestions: this.hasAdditionalQuestions,
        hasSaleableItems: this.hasSaleableItems,
        isSummaryStep: this.isSummaryStep,
        isPaymentStep: this.isPaymentStep,
        stepNumbers: this.stepNumber
      };
    },
    // Helper computed properties for step completion
    additionalQuestionsStepNumber() {
      return this.stepNumber.find((s) => s.type === 'additional')?.number || 0;
    },
    saleableItemsStepNumber() {
      return this.stepNumber.find((s) => s.type === 'saleable')?.number || 0;
    },
    summaryStepNumber() {
      return this.stepNumber.find((s) => s.type === 'summary')?.number || 0;
    },
    paymentStepNumber() {
      return this.stepNumber.find((s) => s.type === 'payment')?.number || 0;
    },
    confirmationStepNumber() {
      return this.stepNumber.find((s) => s.type === 'confirmation')?.number || 0;
    },
    hasCustomEntity() {
      // Check if this is a custom entity based on the registration or rego data
      return this.rego?.isCustomEntity || false;
    },
    fixedTechFee() {
      return this.$store.getters['national/getFixedTechFee'];
    },
  },
  watch: {
    // eslint-disable-next-line func-names
    'value.filtersVisible': function (newVal) {
      this.dialogVisible = newVal;
    },
    dialogVisible(newVal) {
      if (!newVal) {
        this.closeDialog();
      }
    }
  },
  methods: {
    getMembershipPrice(rego) {
      const { regoEntity } = rego;
      const { products } = regoEntity;
      return products.price;
    },
    checkAddress(val, ref = '-MEMBERID') {
      if (!val) this.member.profile.address = val;

      this.$refs[`address${ref}`].focus();
      this.$refs[`form${ref}`].validateField('profile.address.formatted');
    },
    closeDialog() {
      // Reset step completion flags
      this.additionalQuestionsCompleted = false;
      this.saleableItemsCompleted = false;
      this.currentStep = 1;

      // Clean up Google Places styling artifacts
      this.cleanupGooglePlacesInput();

      this.$emit('input', {
        ...this.value,
        filtersVisible: false
      });
      this.$emit('closeDialog');
    },
    nextStep() {
    // For additional questions step
      if (this.currentStep === 1 && this.hasAdditionalQuestions && !this.additionalQuestionsCompleted) {
        // Check if the additionalQuestions component exists and is loaded
        if (this.$refs.additionalQuestions && this.$refs.additionalQuestions.isLoaded) {
          this.$refs.additionalQuestions.updateStep(1);
        } else {
          console.warn('AdditionalQuestions component not ready yet');
        }
        return;
      }

      // For saleable items step
      if (this.currentStep === (this.hasAdditionalQuestions ? 2 : 1)
        && this.hasSaleableItems
        && !this.saleableItemsCompleted) {
        if (this.$refs.saleableItems) {
          this.$refs.saleableItems.updateStep();
        }
        return;
      }

      // For all other steps, just increment
      if (this.currentStep < this.totalSteps) {
        this.currentStep++;
      }
    },
    prevStep() {
      if (this.currentStep > 1) {
        this.currentStep--;
      }
    },
    handleAdditionalQuestionsComplete(registration, step) {
      console.log('Received registration data:', registration); // Debug log
      console.log('Received step:', step); // Debug log

      // Make sure we're updating both objects
      this.registration = {
        ...this.registration,
        meta: registration.meta,
        additionalQs: registration.additionalQs
      };

      this.rego = {
        ...this.rego,
        meta: registration.meta,
        additionalQs: registration.additionalQs
      };

      this.additionalQuestionsCompleted = true;
      this.currentStep++;
    },
    handleSaleableItemsComplete(rego) {
      console.log('selectedItems', rego);
      this.registration.saleableItems = rego.saleableItems;
      this.rego.saleableItems = rego.saleableItems;
      this.saleableItemsCompleted = true;
      this.nextStep();
    },
    manualAddress() {
      this.payment.billingAddress.isManual = !this.payment.billingAddress.isManual;
    },
    fillAddress(address) {
      _.assign(this.payment.billingAddress, address);
    },
    parsePrice(val) {
      const rs = (val / 100).toFixed(2);
      if (val < 0) return `-$${(Math.abs(rs)).toFixed(2)}`;
      return `$${rs}`;
    },
    searchCountry(query) {
      if (query !== '') {
        setTimeout(() => {
          this.countryItems = this.countries.filter((item) => item.label.toLowerCase().indexOf(query.toLowerCase()) > -1);
        }, 200);
      } else {
        this.countryItems = [];
      }
    },
    submitPayment() {
      Promise.all([this.$refs.card && this.$refs.card.validate(), this.$refs.form && this.$refs.form.validate()])
        .then(([cardValid, formValid]) => {
          if (this.requirePayment && (!cardValid || !formValid)) return;

          // issue card token
          const [expiryMonth, expiryYear] = this.payment.card.expiry.replace(/\s/g, '').split('/');

          if (this.payment.billingAddress.isManual) {
            this.payment.billingAddress.formatted = parstAddressFormatted(
              this.payment.billingAddress
            );
          }

          this.$store.commit('loading', { loading: true, text: 'Loading...' });
          this.pin.createCardToken({
            number: this.payment.card.number,
            name: this.payment.card.name,
            expiry_month: expiryMonth,
            expiry_year: expiryYear,
            cvc: this.payment.card.cvc,
            address_line1: this.payment.billingAddress.addressLine1,
            address_line2: this.payment.billingAddress.addressLine2 || '',
            address_city: this.payment.billingAddress.suburb,
            address_state: this.payment.billingAddress.state,
            address_postcode: this.payment.billingAddress.postcode,
            address_country: 'Australia',
          }).then((pinResponse) => {
            // Block International Cards
            // eslint-disable-next-line camelcase, no-unused-vars
            const { token, issuing_country, scheme } = pinResponse;
            // || !_.includes(['master', 'visa'], scheme)
            // eslint-disable-next-line camelcase
            if (!['AU', 'NZ'].includes(issuing_country) && [14, 18].includes(+process.env.VUE_APP_NATIONAL_ID)) {
              this.$store.commit('loading', false);
              this.$message.error('Payment method not supported.');
              return false;
            }

            if (!token) {
              this.$store.commit('loading', false);
              this.$message.error('There has been an error, try again later.');
            }
            // register with card token
            this.pay(token);
          }).catch((err) => {
            this.$store.commit('loading', false);
            const pinError = _.get(err, 'error_description');
            this.$message.error(pinError || 'There has been an error');
          });
        }).catch(() => {});
    },
    pay(token) {
      if (!token) return false;
      this.$store.commit('loading', { loading: true, text: 'Loading...' });
      return this.$http.post('/assemble/api/v1/portal/registrations/renew-payment', {
        cardToken: token,
        paymentTotal: this.payTodayTotal,
        paymentInstallment: this.payTodayTotal,
        registration: this.rego,
      }).then((res) => {
        this.$message.success('Payment Successful');
        this.$set(this, 'registration', res.data.data);
        this.payToday = this.outstanding;
        this.key++;
        this.$store.commit('loading', false);
        // Go to confirmation step instead of redirecting
        this.currentStep = this.totalSteps;
      }).catch((err) => {
        const serverError = _.get(err, 'response.data.message');
        this.$store.commit('loading', false);
        this.$message.error(serverError || 'There has been an error');
      });
    },
    goToDashboard() {
      const pathName = this.hasCustomEntity ? 'custom-dashboard' : 'dashboard';
      this.$router.push({ name: pathName });
    },
    getAddressData(addressData, placeResultData) {
      // Handle Google Places autocomplete selection
      if (addressData && placeResultData) {
        this.payment.billingAddress.formatted = addressData.formatted_address || addressData;

        // Extract address components
        const components = placeResultData.address_components || [];

        // Clear existing address data
        this.payment.billingAddress.addressLine1 = '';
        this.payment.billingAddress.addressLine2 = '';
        this.payment.billingAddress.suburb = '';
        this.payment.billingAddress.state = '';
        this.payment.billingAddress.postcode = '';
        this.payment.billingAddress.country = 'Australia';

        // Parse address components
        components.forEach((component) => {
          const { types } = component;

          if (types.includes('street_number')) {
            this.payment.billingAddress.addressLine1 = component.long_name;
          } else if (types.includes('route')) {
            this.payment.billingAddress.addressLine1 += (this.payment.billingAddress.addressLine1 ? ' ' : '') + component.long_name;
          } else if (types.includes('locality') || types.includes('administrative_area_level_2')) {
            this.payment.billingAddress.suburb = component.long_name;
          } else if (types.includes('administrative_area_level_1')) {
            this.payment.billingAddress.state = component.short_name;
          } else if (types.includes('postal_code')) {
            this.payment.billingAddress.postcode = component.long_name;
          } else if (types.includes('country')) {
            this.payment.billingAddress.country = component.long_name;
          }
        });
      }

      // Clean up any Google Places styling artifacts
      this.$nextTick(() => {
        this.cleanupGooglePlacesInput();
      });
    },
    validateAddress() {
      // Validate the address field and clean up styling
      if (this.$refs.form) {
        this.$refs.form.validateField('profile.address.formatted');
      }

      // Clean up any Google Places styling artifacts
      this.$nextTick(() => {
        this.cleanupGooglePlacesInput();
      });
    },
    cleanupGooglePlacesInput() {
      // Remove any Google Places styling artifacts
      const addressInput = this.$refs.address && this.$refs.address.$el
        ? this.$refs.address.$el.querySelector('input') : null;

      if (addressInput) {
        // Remove any Google Places classes that might be causing visual artifacts
        addressInput.classList.remove('pac-target-input');

        // Reset any inline styles that Google Places might have added
        addressInput.style.removeProperty('box-shadow');
        addressInput.style.removeProperty('outline');
        addressInput.style.removeProperty('border-color');

        // Ensure the input styling is clean
        addressInput.blur();
      }
    },
  },
  created() {
    this.dialogVisible = this.value.filtersVisible;
    this.registration = {
      ...this.registration,
      meta: {},
      saleableItems: []
    };
  },
  filters: {
    cap(value) {
      return value.charAt(0).toUpperCase() + value.slice(1);
    },
  },
};
</script>

<style lang="scss" scoped>
.renew-payment-dialog {
  .dialog-content {
    min-height: 400px;
    position: relative;
    padding: 20px 25px 80px;
    width: 100%;
    background-color: #f8f9fa;

    .section-title {
      font-size: 24px;
      font-weight: 600;
      color: #2c3e50;
      margin-bottom: 24px;
      padding-top: 20px;
    }
  }

  // Summary Section Styling
  .summary-section {
    max-width: 600px;
    margin: 0 auto;
    padding: 20px 15px;

    .summary-card {
      background: white;
      border-radius: 12px;
      box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
      overflow: hidden;
    }

    .summary-group {
      padding: 20px;
      border-bottom: 1px solid #ebeef5;

      &:last-child {
        border-bottom: none;
      }

      .group-title {
        font-size: 16px;
        font-weight: 600;
        color: #2c3e50;
        margin-bottom: 12px;
      }
    }

    .summary-item {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 16px;
      width: 100%;

      &:last-child {
        margin-bottom: 0;
      }

      .item-label {
        color: #909399;
        font-size: 14px;
        width: 140px;
        flex-shrink: 0;
        padding-right: 15px;
      }

      .item-value {
        font-weight: 500;
        color: #2c3e50;
        font-size: 14px;
        flex: 1;
        text-align: right;
        white-space: normal;
        overflow-wrap: break-word;
        word-wrap: break-word;
        max-width: calc(100% - 155px);
        display: block;
      }
    }

    .summary-total {
      background: #f8fafc;
      padding: 20px;
      margin-top: 10px;
      border-top: 1px solid #ebeef5;

      .total-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;

        .total-label {
          font-size: 15px;
          font-weight: 600;
          color: #2c3e50;
        }

        .total-value {
          font-size: 18px;
          font-weight: 700;
          color: #409EFF;
        }
      }

      .transaction-fee {
        margin-top: 8px;
        font-size: 12px;
        color: #909399;
        font-style: italic;
      }
    }
  }

  // Confirmation Section Styling
  .confirmation-section {
    max-width: 600px;
    margin: 0 auto;
    padding: 40px 20px;
    text-align: center;

    .confirmation-card {
      background: white;
      border-radius: 12px;
      box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
      padding: 40px 30px;
      position: relative;
    }

    .success-icon {
      margin-bottom: 20px;

      i {
        font-size: 64px;
        color: #67C23A;
        display: inline-block;
        animation: checkmark 0.5s ease-in-out;
      }
    }

    .confirmation-title {
      font-size: 28px;
      font-weight: 600;
      color: #2c3e50;
      margin-bottom: 16px;
    }

    .confirmation-message {
      font-size: 16px;
      color: #606266;
      line-height: 1.6;
      margin-bottom: 30px;
    }

    .confirmation-details {
      background: #f8fafc;
      border-radius: 8px;
      padding: 20px;
      margin-top: 20px;
      text-align: left;

      .detail-item {
        margin-bottom: 12px;
        font-size: 14px;
        color: #2c3e50;

        &:last-child {
          margin-bottom: 0;
        }

        strong {
          font-weight: 600;
          margin-right: 8px;
        }
      }
    }
  }

  // Animation for success icon
  @keyframes checkmark {
    0% {
      transform: scale(0);
      opacity: 0;
    }
    50% {
      transform: scale(1.2);
      opacity: 1;
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }

  // Steps Styling
  .custom-steps {
    padding: 16px 24px;
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    margin-bottom: 24px;
    overflow-x: auto;
    width: 100%;

    &::-webkit-scrollbar {
      display: none;
    }
    -ms-overflow-style: none;
    scrollbar-width: none;

    ::v-deep {
      .el-steps {
        display: flex;
        min-width: min-content;
        padding: 0 4px;
      }

      .el-step {
        flex: 1;
        display: flex;
        align-items: center;
        padding: 0;
        margin: 0;

        &__head {
          display: none;
        }

        &__main {
          display: flex;
          align-items: center;
          padding: 0;
          margin: 0;
        }

        &__title {
          font-size: 14px;
          line-height: 20px;
          padding: 0;
          margin: 0;
        }

        &__line {
          top: 50%;
          transform: translateY(-50%);
          height: 2px;
          margin: 0;
          right: -50%;
          left: auto;
          width: 100%;
          background-color: #E4E7ED;

          &.is-success {
            background-color: #67C23A;
          }
        }

        &:last-child {
          flex: 0 0 auto;

          .el-step__line {
            display: none;
          }
        }
      }
    }

    .step-wrapper {
      display: flex;
      align-items: center;
      gap: 8px;
      white-space: nowrap;
    }

    .step-icon {
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background-color: #C0C4CC;
      color: white;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 12px;

      &.is-success {
        background-color: #67C23A;
      }

      i {
        font-size: 12px;
      }
    }

    .step-text {
      color: #909399;
      font-size: 14px;
      font-weight: normal;
    }

    .el-step.is-success {
      .step-text {
        color: #67C23A;
      }
    }

    .el-step.is-process {
      .step-icon {
        background-color: #409EFF;
      }
      .step-text {
        color: #409EFF;
        font-weight: 500;
      }
    }
  }

  // Footer Styling
  .dialog-footer {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 20px 25px;
    background: white;
    border-top: 1px solid #ebeef5;
    display: flex;
    justify-content: space-between;
    align-items: center;
    box-shadow: 0 -2px 12px 0 rgba(0, 0, 0, 0.05);

    .left-buttons {
      flex: 0 0 auto;
    }

    .right-buttons {
      flex: 0 0 auto;
      display: flex;
      gap: 12px;

      .el-button {
        min-width: 100px;

        &--primary {
          background-color: var(--highlight-color);
          border-color: var(--highlight-color);

          &:hover {
            background-color: var(--highlight-color);
            border-color: var(--highlight-color);
          }
        }
      }
    }
  }
}

// Dialog Overrides
::v-deep .el-dialog {
  border-radius: 12px;
  overflow: visible;
  width: 90% !important;
  max-width: 1200px !important;
  min-width: 800px !important;

  .el-dialog__header {
    padding: 20px 25px;
    border-bottom: 1px solid #ebeef5;
    margin: 0;

    .el-dialog__title {
      font-size: 18px;
      font-weight: 600;
      color: #2c3e50;
    }
  }

  .el-dialog__body {
    padding: 0;
    width: 100%;
    display: block;
    min-height: 600px;
  }

  // Saleable Items Step Styling
  .step-content {
    ::v-deep .saleable-items {
      .item-card {
        background: white;
        border-radius: 12px;
        box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.08);
        margin-bottom: 20px;
        padding: 20px;
        transition: all 0.3s ease;

        &:hover {
          box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.12);
        }
      }

      .item-image {
        width: 120px;
        height: 120px;
        object-fit: cover;
        border-radius: 8px;
        margin-right: 20px;
      }

      .item-details {
        flex: 1;

        .item-name {
          font-size: 18px;
          font-weight: 600;
          color: #2c3e50;
          margin-bottom: 8px;
        }

        .item-description {
          color: #606266;
          font-size: 14px;
          line-height: 1.5;
          margin-bottom: 12px;
        }

        .item-price {
          font-size: 16px;
          font-weight: 600;
          color: #409EFF;
        }
      }

      .quantity-controls {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-top: 15px;

        .quantity-label {
          font-size: 14px;
          font-weight: 500;
          color: #2c3e50;
          min-width: 60px;
        }

        .el-input-number {
          width: 120px !important;

          .el-input__inner {
            height: 40px !important;
            line-height: 40px !important;
            font-size: 16px !important;
            font-weight: 500;
            text-align: center;
            border: 2px solid #dcdfe6;
            border-radius: 8px;

            &:focus {
              border-color: #409EFF;
              box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.2);
            }
          }

          .el-input-number__decrease,
          .el-input-number__increase {
            width: 35px !important;
            height: 38px !important;
            line-height: 38px !important;
            font-size: 16px !important;
            border: 2px solid #dcdfe6;

            &:hover {
              background-color: #409EFF;
              color: white;
              border-color: #409EFF;
            }
          }
        }
      }

      .item-row {
        display: flex;
        align-items: flex-start;
        gap: 20px;
        padding: 20px;
        border: 2px solid #f0f0f0;
        border-radius: 12px;
        margin-bottom: 16px;
        transition: all 0.3s ease;

        &:hover {
          border-color: #409EFF;
          background-color: #f8faff;
        }
      }

      .store-title {
        font-size: 24px;
        font-weight: 600;
        color: #2c3e50;
        margin-bottom: 20px;
        text-align: center;
      }

      .store-description {
        color: #606266;
        font-size: 16px;
        text-align: center;
        margin-bottom: 30px;
        line-height: 1.6;
      }
    }
  }
}

// Mobile Breakpoints
@media screen and (max-width: 768px) {
  .custom-steps {
    padding: 12px 16px;

    ::v-deep {
      .el-step {
        margin-right: 24px;

        &__title {
          font-size: 13px;
        }
      }

      .step-wrapper {
        gap: 6px;
      }

      .step-icon {
        width: 18px;
        height: 18px;
        font-size: 11px;
      }

      .step-text {
        font-size: 13px;
      }
    }
  }
}

@media screen and (max-width: 768px) {
  ::v-deep .el-dialog {
    width: 95% !important;
    min-width: 320px !important;
    margin: 15px auto !important;
  }
}

@media screen and (max-width: 480px) {
  ::v-deep .el-dialog {
    width: 98% !important;
    min-width: 300px !important;
    margin: 10px auto !important;
  }

  .renew-payment-dialog {
    .dialog-content {
      padding: 15px 15px 80px;
    }

    .summary-section {
      padding: 10px 0;

      .section-title {
        font-size: 20px;
        margin-bottom: 16px;
        padding-top: 10px;
      }

      .summary-card {
        margin: 0 15px;
      }

      .summary-group {
        padding: 15px;

        .group-title {
          font-size: 15px;
          margin-bottom: 10px;
        }
      }

      .summary-item {
        flex-wrap: wrap;
        margin-bottom: 12px;

        .item-label {
          width: 100%;
          margin-bottom: 4px;
          padding-right: 0;
        }

        .item-value {
          width: 100%;
          max-width: 100%;
          text-align: left;
          padding-left: 0;
        }
      }

      .summary-total {
        margin-top: 8px;
        padding: 15px;

        .total-item {
          .total-label {
            font-size: 14px;
          }

          .total-value {
            font-size: 16px;
          }
        }
      }
    }

    .dialog-footer {
      padding: 15px;

      .right-buttons {
        gap: 8px;

        .el-button {
          min-width: 80px;
          padding: 8px 15px;
          font-size: 13px;
        }
      }
    }
  }

  .custom-steps {
    padding: 10px 12px;

    ::v-deep {
      .el-step {
        margin-right: 16px;

        &__title {
          font-size: 12px;
        }
      }

      .step-wrapper {
        gap: 4px;
      }

      .step-icon {
        width: 16px;
        height: 16px;
        font-size: 10px;
      }

      .step-text {
        font-size: 12px;
      }
    }
  }

  .confirmation-section {
    padding: 20px 10px;

    .confirmation-card {
      padding: 30px 20px;
      margin: 0 10px;
    }

    .success-icon i {
      font-size: 48px;
    }

    .confirmation-title {
      font-size: 22px;
      margin-bottom: 12px;
    }

    .confirmation-message {
      font-size: 14px;
      margin-bottom: 20px;
    }

    .confirmation-details {
      padding: 15px;
      margin-top: 15px;

      .detail-item {
        margin-bottom: 10px;
        font-size: 13px;
      }
    }
  }
}

// Utility Classes
.w-100 {
  width: 100%;
}

.mb-30 {
  margin-bottom: 30px;
}

// Global Google Places Autocomplete z-index fix
:global(.pac-container) {
  z-index: 999999 !important;
  position: absolute !important;
}

// Completely remove Google Places input styling
:global(.pac-target-input) {
  box-shadow: none !important;
  outline: none !important;
  border: 1px solid #dcdfe6 !important;
  border-color: #dcdfe6 !important;
  background: white !important;
  color: #606266 !important;
  font-family: inherit !important;
  font-size: inherit !important;
}

// Override Google Places styling on all inputs
:global(input.pac-target-input) {
  box-shadow: none !important;
  outline: none !important;
  border: 1px solid #dcdfe6 !important;
  border-color: #dcdfe6 !important;
  background: white !important;
  color: #606266 !important;
  font-family: inherit !important;
  font-size: inherit !important;
}

// Clean Google Places input styling locally
::v-deep .google-auto-complete input,
::v-deep .auto-complete input {
  box-shadow: none !important;
  outline: none !important;
  border: 1px solid #dcdfe6 !important;
  border-color: #dcdfe6 !important;
  background: white !important;
  color: #606266 !important;
  font-family: inherit !important;
  font-size: inherit !important;

  &.pac-target-input {
    box-shadow: none !important;
    outline: none !important;
    border: 1px solid #dcdfe6 !important;
    border-color: #dcdfe6 !important;
    background: white !important;
    color: #606266 !important;
    font-family: inherit !important;
    font-size: inherit !important;
  }

  &:focus {
    border-color: #409EFF !important;
    box-shadow: none !important;
    outline: none !important;
  }
}

// Element UI input overrides to prevent Google Places interference
::v-deep .el-input__inner {
  &.pac-target-input {
    box-shadow: none !important;
    outline: none !important;
    border: 1px solid #dcdfe6 !important;
    border-color: #dcdfe6 !important;
    background: white !important;
    color: #606266 !important;
    font-family: inherit !important;
    font-size: inherit !important;

    &:focus {
      border-color: #409EFF !important;
      box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.2) !important;
      outline: none !important;
    }
  }
}

// Completely disable Google Places styling
:global(.pac-target-input:focus) {
  border-color: #409EFF !important;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.2) !important;
  outline: none !important;
}

// Google Places Autocomplete z-index fix
.pac-container {
  z-index: 999999 !important;
  position: absolute !important;
}

// Ensure the autocomplete appears above Element UI components
::v-deep .el-dialog__wrapper {
  .pac-container {
    z-index: 999999 !important;
    position: absolute !important;
  }
}
</style>
